import alt from 'components/Dispatcher';
import immutableStore from 'utils/immutableStore';
import DepartmentActions from 'actions/department/DepartmentActions';
import DepartmentFormActions from 'actions/DepartmentFormActions';
import DepartmentStore from 'stores/department/DepartmentStore';
import ToastStore from '@store/toast/ToastStore';
import Department from 'records/Department';
import {Set, Record} from 'immutable';

const errorSavingMessage =
  'There was a problem communicating with the server when saving the department.';

const State = Record({
  isCreateDialogOpen: false,
  isDepartmentSaving: false,
  isDepartmentLoading: true,
  wasFetchError: false,
  department: null,
  stagingDepartment: null,
  toggledFields: new Department(),
  errors: new Department(),
  stagingErrors: new Department(),
  touchedFields: new Set(),
  wasSubmitRequested: false,
  isHelpVisible: false,
});

@immutableStore
class DepartmentFormStore {
  constructor() {
    this.state = State();
    this.bindActions(DepartmentFormActions);
    this.bindActions(DepartmentActions);
  }

  onCreateDepartmentLoading() {
    this.setState({isDepartmentSaving: true});
  }

  onCreateDepartmentSuccess(createdDepartment) {
    this.setState({
      isDepartmentSaving: false,
      isCreateDialogOpen: false,
    });
    DepartmentStore.addDepartment(createdDepartment);
  }

  onCreateDepartmentError() {
    this.setState({isDepartmentSaving: false});
    ToastStore.addToast({
      type: 'error',
      message: errorSavingMessage,
    });
  }

  onFetchDepartment() {
    this.setState({
      isDepartmentLoading: true,
      wasFetchError: false,
      department: null,
    });
  }

  onFetchDepartmentSuccess(department) {
    this.setState({
      isDepartmentLoading: false,
      department: department,
      stagingDepartment: department,
      // what in the world is going on here
      toggledFields: new Department(),
      stagingErrors: new Department(),
    });
  }

  onFetchDepartmentError() {
    this.setState({
      wasFetchError: true,
      isDepartmentLoading: false,
    });
  }

  onToggleEditField(field) {
    this.setState({
      toggledFields: this.state.toggledFields.set(field, true),
      stagingDepartment: this.state.stagingDepartment.set(field, this.state.department[field]),
    });
    this.validateStagingDepartment();
  }

  onDiscardStagingField(field) {
    this.setState({
      toggledFields: this.state.toggledFields.set(field, false),
    });
  }

  onCommitStagingField(field) {
    this.setState({isDepartmentSaving: true});
  }

  onCommitStagingFieldSuccess([field, updatedDepartment]) {
    this.setState({
      toggledFields: this.state.toggledFields.set(field, false),
      department: updatedDepartment,
      isDepartmentSaving: false,
    });
    ToastStore.addToast({
      type: 'success',
      time: 2000,
      message: 'Department updated!',
    });
  }

  onCommitStagingFieldError() {
    this.setState({isDepartmentSaving: false});
    ToastStore.addToast({
      type: 'error',
      message: errorSavingMessage,
    });
  }

  onEditStagingDepartment([property, value]) {
    this.setState({
      stagingDepartment: this.state.stagingDepartment.set(property, value),
    });
    this.validateStagingDepartment();
  }

  onEditDepartment([property, value]) {
    this.setState({
      department: this.state.department.set(property, value),
    });
    this.onValidateDepartment();
  }

  validateStagingDepartment() {
    this.validateModel(this.state.stagingDepartment, 'stagingErrors');
  }

  onValidateDepartment() {
    this.validateModel(this.state.department, 'errors');
  }

  validateModel(model, errorProperty) {
    const {departmentProfiles} = DepartmentStore.getState();
    const nameErrors = [];

    (model.name || '').trim() === '' && nameErrors.push('This field is required');

    departmentProfiles.some(profile => profile.name === model.name && profile.id !== model.id) &&
      nameErrors.push('This name is already taken');

    const errors =
      nameErrors.length > 0
        ? // reuse department model for errors,
          // whose property names mirror the model
          new Department({
            name: nameErrors,
          })
        : null;

    this.setState({
      [errorProperty]: errors,
    });
  }

  onCreateDepartment() {
    this.setState({isDepartmentSaving: true});
  }

  onOpenCreateDialog() {
    this.setState({
      isCreateDialogOpen: true,
      touchedFields: new Set(),
      wasSubmitRequested: false,
      department: new Department(),
      stagingDepartment: new Department(),
    });
    this.onValidateDepartment();
  }

  onCloseCreateDialog() {
    if (!this.state.isDepartmentSaving) {
      this.setState({isCreateDialogOpen: false});
    }
  }

  onFieldTouched(property) {
    this.setState({
      touchedFields: this.state.touchedFields.add(property),
    });
  }

  onRequestSubmit() {
    this.setState({wasSubmitRequested: true});
  }

  toggleHelp() {
    this.setState({isHelpVisible: !this.state.isHelpVisible});
  }

  onUpdateDepartment() {
    this.setState({
      isDepartmentSaving: true,
    });
  }

  onUpdateDepartmentSuccess(department) {
    this.setState({
      department,
      isDepartmentSaving: false,
    });
  }

  onUpdateDepartmentError() {
    this.setState({
      isDepartmentSaving: false,
    });
  }
}

export default alt.createStore(DepartmentFormStore);
